import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { company: "", client_code: "", contact_person: "", email: "", phone: "", industry: "", status: "ACTIVE", password: "", logo_url: "", description: "" };

export default function AdminClients() {
  const [clients, setClients] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [resetPassword, setResetPassword] = useState("");

  const load = () => api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => setClients([]));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c, password: "" }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/clients/${editing.id}`, form);
        toast.success("Client updated");
      } else {
        await api.post("/admin/clients", form);
        toast.success("Client created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.company}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/clients/${c.id}`);
      toast.success("Client deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/clients/${resetFor.id}/reset-password`, { password: resetPassword });
      toast.success(`Password reset for ${resetFor.company}`);
      setResetFor(null);
      setResetPassword("");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  if (!clients) return <Skeleton className="h-64 bg-white/5" />;

  return (
    <div className="space-y-6" data-testid="admin-clients-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="section-label">Client Management</p>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight mt-1">Clients</h2>
        </div>
        <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-500" data-testid="create-client-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Client
        </Button>
      </div>

      <div className="card-surface overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="clients-table">
            <thead>
              <tr className="text-left text-[10px] tracking-widest text-slate-500 uppercase border-b border-white/5 bg-secondary/20">
                <th className="px-5 py-3.5">Company</th><th className="px-5 py-3.5">Code</th>
                <th className="px-5 py-3.5">Contact</th><th className="px-5 py-3.5">Industry</th>
                <th className="px-5 py-3.5">Campaigns</th><th className="px-5 py-3.5">Geofences</th>
                <th className="px-5 py-3.5">Status</th><th className="px-5 py-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {clients.map((c) => (
                <tr key={c.id} className="text-slate-300 hover:bg-white/[0.02]" data-testid={`client-row-${c.client_code}`}>
                  <td className="px-5 py-3.5 font-semibold text-white">{c.company}</td>
                  <td className="px-5 py-3.5 font-mono text-xs">{c.client_code}</td>
                  <td className="px-5 py-3.5 text-xs">{c.contact_person}<br /><span className="text-slate-500">{c.email}</span></td>
                  <td className="px-5 py-3.5 text-xs">{c.industry}</td>
                  <td className="px-5 py-3.5">{c.campaign_count}</td>
                  <td className="px-5 py-3.5">{c.geofence_count}</td>
                  <td className="px-5 py-3.5">
                    <span className={`text-[10px] font-bold tracking-widest px-2 py-1 rounded border ${c.status === "ACTIVE" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" : "bg-slate-500/10 text-slate-400 border-slate-500/30"}`}>{c.status}</span>
                  </td>
                  <td className="px-5 py-3.5 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" className="h-8 text-slate-300" onClick={() => openEdit(c)} data-testid={`edit-client-${c.client_code}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="h-8 text-amber-400" onClick={() => setResetFor(c)} data-testid={`reset-client-${c.client_code}`}><KeyRound className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="h-8 text-red-400" onClick={() => remove(c)} data-testid={`delete-client-${c.client_code}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="client-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-white">{editing ? "Edit Client" : "Create Client"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="grid sm:grid-cols-2 gap-4 mt-2">
            <div><Label className="text-slate-300">Client Company *</Label><Input required value={form.company} onChange={set("company")} className="mt-1.5 bg-secondary/50 border-white/10" data-testid="client-form-company" /></div>
            <div><Label className="text-slate-300">Client Code *</Label><Input required value={form.client_code} onChange={set("client_code")} className="mt-1.5 bg-secondary/50 border-white/10" data-testid="client-form-code" /></div>
            <div><Label className="text-slate-300">Contact Person</Label><Input value={form.contact_person} onChange={set("contact_person")} className="mt-1.5 bg-secondary/50 border-white/10" data-testid="client-form-contact" /></div>
            <div><Label className="text-slate-300">Email</Label><Input type="email" value={form.email} onChange={set("email")} className="mt-1.5 bg-secondary/50 border-white/10" data-testid="client-form-email" /></div>
            <div><Label className="text-slate-300">Phone</Label><Input value={form.phone} onChange={set("phone")} className="mt-1.5 bg-secondary/50 border-white/10" data-testid="client-form-phone" /></div>
            <div><Label className="text-slate-300">Industry</Label><Input value={form.industry} onChange={set("industry")} className="mt-1.5 bg-secondary/50 border-white/10" data-testid="client-form-industry" /></div>
            <div>
              <Label className="text-slate-300">Account Status</Label>
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger className="mt-1.5 bg-secondary/50 border-white/10" data-testid="client-form-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-white/10">
                  <SelectItem value="ACTIVE">ACTIVE</SelectItem>
                  <SelectItem value="DISABLED">DISABLED</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">{editing ? "New Password (leave blank to keep)" : "Password *"}</Label>
              <Input type="password" required={!editing} value={form.password} onChange={set("password")} className="mt-1.5 bg-secondary/50 border-white/10" data-testid="client-form-password" />
            </div>
            <div className="sm:col-span-2"><Label className="text-slate-300">Logo URL</Label><Input value={form.logo_url} onChange={set("logo_url")} className="mt-1.5 bg-secondary/50 border-white/10" data-testid="client-form-logo" /></div>
            <div className="sm:col-span-2"><Label className="text-slate-300">Company Description</Label><Textarea rows={3} value={form.description} onChange={set("description")} className="mt-1.5 bg-secondary/50 border-white/10" data-testid="client-form-description" /></div>
            <div className="sm:col-span-2">
              <Button type="submit" disabled={saving} className="w-full bg-blue-600 hover:bg-blue-500" data-testid="client-form-submit">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Save Changes" : "Create Client"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetFor} onOpenChange={() => setResetFor(null)}>
        <DialogContent className="bg-popover border-white/10" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-white">Reset password — {resetFor?.company}</DialogTitle>
          </DialogHeader>
          <form onSubmit={doReset} className="space-y-4 mt-2">
            <Input type="password" required minLength={6} placeholder="New password" value={resetPassword} onChange={(e) => setResetPassword(e.target.value)}
              className="bg-secondary/50 border-white/10" data-testid="reset-password-input" />
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-500" data-testid="reset-password-submit">Reset Password</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
